import React, { createContext, useContext, useState, ReactNode } from "react";
import { Alert as MuiAlert, AlertProps, Snackbar } from "@mui/material";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

type AlertSeverity = "success" | "info" | "warning" | "error";

interface AlertContextType {
  showAlert: (
    severity: AlertSeverity,
    message: string,
    duration?: number,
  ) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [alertProps, setAlertProps] = useState<{
    severity: AlertSeverity;
    message: string;
    duration: number;
  }>({ severity: "info", message: "", duration: 2000 });

  const showAlert = (
    severity: AlertSeverity,
    message: string,
    duration = 2000,
  ) => {
    setAlertProps({ severity, message, duration });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={alertProps.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "300px",
            "&.MuiAlert-filledSuccess": {
              // backgroundColor: '#2e7d32',
            },
            "&.MuiAlert-filledError": {
              // backgroundColor: '#d32f2f',
            },
            "&.MuiAlert-filledWarning": {
              // backgroundColor: '#ed6c02',
            },
            "&.MuiAlert-filledInfo": {
              // backgroundColor: '#0288d1',
            },
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={alertProps.severity}
          sx={{ width: "100%" }}
        >
          {alertProps.message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};
